import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import * as TutorReducer from 'src/app/reducers/room/tutor.reducer';
import { useSoundContext } from 'src/common/contexts/sound-context';
import { addSocketListener, removeSocketListener } from 'src/common/sockets';
import nudgeReceived from 'src/images/audio/nudge-received.mp3';

import { type TutorNudge, TutorSocketEventType } from '../tutor.types';

import { useTutorSocketEvents } from './use-tutor-socket';

interface UseTutorNudgeProps {
  roomId?: string;
  roomCode: string;
}

type HandleReceiveNudge = (
  data: Omit<TutorNudge, 'status'> & {
    tutorSessionId: string;
  },
  source: 'nudge' | 'hint',
) => void;

interface UseTutorNudgeConnectionInitProps {
  handleReceiveNudge: HandleReceiveNudge;
}

export const useTutorNudgeEvents = ({ roomId, roomCode }: UseTutorNudgeProps) => {
  const {
    chat: { sessionId: tutorSessionId, open: isTutorOpen, nudgesEnabled = true, nudge },
  } = useAppSelector((state) => state.tutor);
  const dispatch = useAppDispatch();
  const { playSoundIfEnabled } = useSoundContext();
  const { readMessages } = useTutorSocketEvents({
    roomId,
    roomCode,
  });

  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const playNudgeSound = useCallback(async () => {
    if (!nudgesEnabled) {
      return;
    }

    try {
      await playSoundIfEnabled(nudgeReceived);
    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }, [nudgesEnabled, playSoundIfEnabled]);

  const setNudgeToIdle = useCallback(() => {
    dispatch(TutorReducer.setNudge({ status: 'idle', preview: '', message: '', source: undefined }));
  }, [dispatch]);

  const setNudgeToInactive = useCallback(() => {
    dispatch(TutorReducer.setNudgeToInactive());
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
    }
    nudgeTimerRef.current = setTimeout(() => {
      setNudgeToIdle();
    }, 60 * 1000);
  }, [dispatch, setNudgeToIdle]);

  const clearNudge = useCallback(() => {
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
    if (nudge.status !== 'idle') {
      setNudgeToIdle();
    }
  }, [setNudgeToIdle, nudge.status]);

  const handleNudgeClose = useCallback(() => {
    if (nudge.source === 'hint') {
      clearNudge();
      return;
    }

    if (nudge.status === 'active') {
      setNudgeToInactive();
      return;
    }

    if (nudge.status === 'inactive') {
      clearNudge();
    }
  }, [nudge.source, nudge.status, clearNudge, setNudgeToInactive]);

  const handleReceiveNudge = useCallback<HandleReceiveNudge>(
    (data, source = 'nudge') => {
      if (!data.tutorSessionId && !tutorSessionId) {
        return;
      }

      if (data.tutorSessionId) {
        dispatch(TutorReducer.setTutorSessionId(data.tutorSessionId));
      }

      void playNudgeSound();

      if (isTutorOpen) {
        if (source === 'hint') {
          if (nudgeTimerRef.current) {
            clearTimeout(nudgeTimerRef.current);
            nudgeTimerRef.current = null;
          }
          setNudgeToIdle();
        }
      } else {
        dispatch(
          TutorReducer.setNudge({
            message: data.message,
            preview: data.preview,
            status: 'active',
            source,
          }),
        );
        if (nudgeTimerRef.current) {
          clearTimeout(nudgeTimerRef.current);
        }

        if (source === 'nudge') {
          nudgeTimerRef.current = setTimeout(() => setNudgeToInactive(), 60 * 1000);
        } else {
          nudgeTimerRef.current = setTimeout(() => clearNudge(), 5 * 1000);
        }
      }

      if (source === 'nudge') {
        readMessages({ source: 'nudge', sessionId: data.tutorSessionId });
      }
    },
    [
      tutorSessionId,
      dispatch,
      playNudgeSound,
      setNudgeToInactive,
      readMessages,
      isTutorOpen,
      setNudgeToIdle,
      clearNudge,
    ],
  );

  useEffect(() => {
    return () => {
      if (nudgeTimerRef.current) {
        clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
      }

      setNudgeToIdle();
    };
  }, [setNudgeToIdle]);

  return { clearNudge, handleReceiveNudge, setNudgeToInactive, handleNudgeClose };
};

export const useTutorNudgeConnectionInit = ({ handleReceiveNudge }: UseTutorNudgeConnectionInitProps) => {
  useEffect(() => {
    const handleReceiveNudgeFromSocket = (data: Omit<TutorNudge, 'status'> & { tutorSessionId: string }) => {
      handleReceiveNudge(data, 'nudge');
    };

    addSocketListener(TutorSocketEventType.RECEIVE_NUDGE, handleReceiveNudgeFromSocket);

    return () => {
      removeSocketListener(TutorSocketEventType.RECEIVE_NUDGE);
    };
  }, [handleReceiveNudge]);
};
